export type CalendarMode = "month" | "list";

export interface EventCalendarSearch {
  month?: string;
  b?: string;
  mode?: CalendarMode;
  day?: string;
}

export interface EventDetailSearch extends EventCalendarSearch {
  at?: string;
  day?: string;
}

export function validateEventCalendarSearch(
  search: Record<string, unknown>,
): EventCalendarSearch {
  const month = validMonth(search.month);
  const board = typeof search.b === "string" ? search.b : "";
  const mode =
    search.mode === "month" || search.mode === "list" ? search.mode : undefined;
  const day = validDate(search.day);
  return {
    ...(month ? { month } : {}),
    ...(board ? { b: board } : {}),
    ...(mode ? { mode } : {}),
    ...(day ? { day } : {}),
  };
}

export function validateEventDetailSearch(
  search: Record<string, unknown>,
): EventDetailSearch {
  const calendar = validateEventCalendarSearch(search);
  const at = validInstant(search.at);
  const day = validDate(search.day);
  return {
    ...calendar,
    ...(at ? { at } : {}),
    ...(day ? { day } : {}),
  };
}

export function calendarSearchFromState(state: {
  month: string;
  board: string;
  mode: CalendarMode;
  expandedDay?: string;
}): EventCalendarSearch {
  return {
    month: state.month,
    ...(state.board ? { b: state.board } : {}),
    mode: state.mode,
    ...(state.expandedDay ? { day: state.expandedDay } : {}),
  };
}

export function detailSearchFromState(
  at: string,
  state: {
    month: string;
    board: string;
    mode: CalendarMode;
    expandedDay?: string;
  },
): EventDetailSearch {
  return {
    ...calendarSearchFromState(state),
    at,
  };
}

export function resolveEventCalendarMonth(
  search: EventCalendarSearch,
  occurrenceMonth?: string,
): string | undefined {
  return search.month ?? occurrenceMonth;
}

export function toggleExpandedCalendarDay(
  expandedDay: string | undefined,
  selectedDay: string,
): string | undefined {
  return expandedDay === selectedDay ? undefined : selectedDay;
}

export function eventNavigationVisibility(
  loggedIn: boolean,
  hasEventCalendarFeature: boolean,
): { showMyEvents: boolean; showRegistration: boolean } {
  return {
    showMyEvents: loggedIn,
    showRegistration: hasEventCalendarFeature,
  };
}

export function resolveEventDetailReturnSearch(
  search: EventDetailSearch,
  calendar: {
    month: string;
    board: string;
    mode: CalendarMode;
    expandedDay?: string;
  },
): EventCalendarSearch {
  const month =
    resolveEventCalendarMonth(search, calendar.month) ?? calendar.month;
  const expandedDay = search.day ?? calendar.expandedDay;
  return calendarSearchFromState({
    month,
    board: search.b ?? calendar.board,
    mode: search.mode ?? calendar.mode,
    expandedDay: expandedDay?.startsWith(`${month}-`) ? expandedDay : undefined,
  });
}

function validMonth(value: unknown): string | undefined {
  if (typeof value !== "string" || !/^\d{4}-\d{2}$/u.test(value))
    return undefined;
  try {
    return Temporal.PlainYearMonth.from(value).toString();
  } catch {
    return undefined;
  }
}

function validInstant(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  try {
    return Temporal.Instant.from(value).toString();
  } catch {
    return undefined;
  }
}

function validDate(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  try {
    return Temporal.PlainDate.from(value).toString();
  } catch {
    return undefined;
  }
}
